import { Component, HostListener } from '@angular/core';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { StatusUtils } from '../../../../utils/status-utils';
import { SignalRService } from '../../../../core/services/signalR/signalR';
import { environment } from '../../../../../environments/environment';
import { PatientListModel } from '../../../../core/models/patient/patient-list.model';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DateFormatterUtil } from '../../../../utils/date-format-utils';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { nlLocale } from 'ngx-bootstrap/locale';
import { ChatStatus } from '../../../../../enums/chat-status.enum';
import { TablesComponent } from '../../../components/tables/tables.component';
import { ColumnModel } from '../../../../core/models/table/column.model';
import { FilterModel } from '../../../../core/models/filters/filters.model';
import { SortModel } from '../../../../core/models/filters/sort.model';
import { ToastService } from '../../../../core/services/toast.service';
import { UserRole } from '../../../../../enums/roles.enum';

defineLocale('nl', nlLocale);

@Component({
  selector: 'app-patient-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    BsDatepickerModule,
    TablesComponent,
  ],
  templateUrl: './patient-list.component.html',
  styleUrl: './patient-list.component.scss',
  providers: [DatePipe, provideAnimations()],
})
export class PatientListComponent {
  ChatStatus = ChatStatus;
  currentUser: UserSessionModel | null = null;
  patients: PatientListModel[] = [];
  totalPatient: number = 0;
  totalPage: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  pageHeight: number = 10;
  currentPage: number = 1;
  patientListLoading: boolean = true;
  organizationList: any = [];
  caseStatuses = StatusUtils?.getFilterCaseStatuses();
  maxDate = new Date();
  chatStatuses: number[] = [];
  tableHeader: string = '';
  footerText: string = '';
  columns: ColumnModel[] = [];
  url: string = `${environment.apiLocations.patient}/Patient`;
  signalrModel: any = null;
  columnWidths: string[] = [
    'w-80',
    'w-125',
    'w-200',
    'w-150',
    'w-125',
    'w-200',
  ];

  filters: FilterModel[] = [
    //Derm No
    {
      type: 'input',
      label: 'dermNo',
      placeholder: 'filterByDermNo',
      filterId: 'PIDNo',
      value: null,
    },
    //StartDate
    {
      type: 'date',
      label: 'startCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'StartCreatedDateTime',
      value: null,
    },
    //EndCreateDate
    {
      type: 'date',
      label: 'endCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'EndCreatedDateTime',
      value: null,
    },
    //PatientName
    {
      type: 'input',
      label: 'patientName',
      placeholder: 'filterByNameAndSurname',
      filterId: 'NameSurname',
      value: null,
    },
    //Status
    {
      type: 'select',
      label: 'status',
      placeholder: 'selectStatus',
      filterId: 'Status',
      value: null,
    },
  ];
  sort: SortModel | null = null;

  constructor(
    private currentUserService: CurrentUserService,
    private patientsService: PatientsService,
    private signalRService: SignalRService,
    private translate: TranslateService,
    private bsLocaleService: BsLocaleService,
    private toastService: ToastService,
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    this.createPatientListConnection();
    this.createPatientStatusConnection();
    this.bsLocaleService.use(userLang);

    this.columns = [
      { key: 'pidNo', translateKey: 'dermNo' },
      { key: 'createdDate', translateKey: 'createdDate' },
      { key: 'nameAndSurname', translateKey: 'patientName' },
      { key: 'customerCode', translateKey: 'customerCode' },
      { key: 'createdBy', translateKey: 'createdBy' },
      { key: 'patientStatus', translateKey: 'status' },
    ];
    setTimeout(() => {
      this.tableHeader = this.translate.instant('patientList');
      this.footerText = this.translate.instant('footerText.patients');
    }, 100);
  }



  async ngOnInit() {
    await StatusUtils?.initializeTranslateService(this.translate);
    this.caseStatuses = StatusUtils?.getFilterCaseStatuses();
    this.currentUser = this.currentUserService.GetSession();
    if (
      this.currentUser?.role === UserRole.Admin ||
      this.currentUser?.role === UserRole.OrganizationAdmin ||
      this.currentUser?.role === UserRole.GeneralPractitioner
    ) {
      this.columns.push({
        key: 'actions',
        translateKey: 'actions',
        actionTypes: ['delete'],
      });
    }
    this.calculatePageSize();
    this.getPatientList(this.currentPage, this.pageSize);

  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    const newHeight = window.innerHeight;

    if (newHeight !== this.pageHeight) {
      this.calculatePageSize();
    }
  }

  private calculatePageSize(): void {
    const pageHeight = window.innerHeight;
    this.pageHeight = pageHeight;
    if (pageHeight < 600) {
      this.pageSize = 5;
    } else if (pageHeight < 1000) {
      this.pageSize = 10;
    } else if (pageHeight < 1400) {
      this.pageSize = 25;
    } else {
      this.pageSize = 50;
    }
    if (this.filters.some((filter) => !!filter.value)) {
      const dateFilters = this.filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });
      this.getPatientList(
        1,
        this.pageSize,
        this.filters,
        this.sort ? this.sort : null
      );
    } else {
      this.getPatientList(
        this.currentPage,
        this.pageSize,
        null,
        this.sort ? this.sort : null
      );
    }
  }

  protected async createPatientListConnection() {
    const signalRBasePath = `${environment.apiLocations.patient}`.split(
      '/api/v1'
    )[0];
    const hubUrl = 'patient-hub';
    this.signalRService.connect(hubUrl, signalRBasePath);
    this.signalRService.on(hubUrl, 'receivePatientCreated', signalRBasePath, (patient) => {
      const parsedPatient = JSON.parse(patient);
      if (parsedPatient) {
        const newPatient = {
          pidNo: parsedPatient.PIDNo,
          createdDate: parsedPatient.CreatedDate,
          name: parsedPatient.Name,
          lastName: parsedPatient.LastName,
          customerCode: parsedPatient.CustomerCode,
          createdBy: parsedPatient.CreatedBy,
          patientStatus: parsedPatient.PatientStatus,
          patientId: parsedPatient.PatientId,
          chatStatuses: [],
        };
        this.totalPatient++;
        this.patients = [newPatient, ...this.patients];
      }
    });
  }

  protected async createPatientStatusConnection() {
    const signalRBasePath = `${environment.apiLocations.patient}`.split(
      '/api/v1'
    )[0];
    const hubUrl = 'patient-hub';
    this.signalRService.connect(hubUrl, signalRBasePath);
    this.signalRService.on(hubUrl, 'receivePatientUpdated', signalRBasePath, (message) => {
      const parsedJson = JSON.parse(message);
      this.signalrModel = parsedJson;

    });
  }

  filterPatientList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getPatientList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getPatientList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getPatientList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  getPatientList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): void {
    this.patientListLoading = true;
    this.patientsService.patientList(page, size, filters, sort).subscribe({
      next: (res) => {
        this.patients = res.items;
        this.totalPatient = res.count;
        this.totalPage = res.pages;
        this.pageSize = res.size;
        this.currentPage = page;
        this.patientListLoading = false;
        this.chatStatuses = res.items.map(
          (patient: any) => patient.chatStatuses
        );
      },
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getPatientList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getPatientList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  getSortByValue(column: string): string {
    switch (column) {
      case 'pidNo':
        return 'PIDNo';
      case 'nameAndSurname':
        return 'Name';
      case 'customerCode':
        return 'PatientRecord.CustomerCode';
      case 'createdBy':
        return 'CreatedUser';
      case 'patientStatus':
        return 'PatientRecord.PatientRecordCases.PatientStatus';
      default:
        return column;
    }
  }

  actionButtonClick(actionType: string, row: any): void {
    switch (actionType) {
      case 'delete':
        this.deletePatient(row.patientId);
        break;
    }
  }

  deletePatient(id: string): void {
    this.patientsService.deletePatient(id).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successDeletePatientDesc'),
          'success'
        );
        this.getPatientList(1, this.pageSize, this.filters);
      },
    });
  }
}
